import {
  apiService,
  extractSyncResponse,
  getModelCount,
  IAssessmentResult,
  IAssessments,
  ICheckedStudentExam,
  ICheckPoint,
  ICheckPointResult,
  IStudentResponse,
  IStudentSubjectAnswerAnalysis,
  ICheckedStudentExamV2,
  ISyncChangesResponse,
  IUser,
  studentDB,
} from '@taleemabad/db';

const PAGE_SIZE = 100;
interface ICheckpointAssessmentResponse {
  timestamp: string;
  checkpoint: ISyncChangesResponse<ICheckPoint>;
  checkpointResults: ISyncChangesResponse<ICheckPointResult>;
  checkpointAssessments: ISyncChangesResponse<IAssessments>;
  studentsData: ISyncChangesResponse<IStudentResponse>;
  studentsAssessmentData: ISyncChangesResponse<IAssessmentResult>;
  usersData: ISyncChangesResponse<IUser>;
  checkedStudentExam: ISyncChangesResponse<ICheckedStudentExam>;
  studentSubjectAnswerAnalysis: ISyncChangesResponse<IStudentSubjectAnswerAnalysis>;
  checkedStudentExamV2: ISyncChangesResponse<ICheckedStudentExamV2>;
}

// End-point for Student Report Card
export const fetchStudentAssessmentDataV1 = async (
  timestamp: string | null,
  pageNumber: number = 1,
): Promise<boolean> => {
  // trigger api call start
  const apiTimeStart = Date.now();
  const response = await apiService.get<ICheckpointAssessmentResponse>(
    `/api/v1/student-checkpoint-assessment-data-sync/?page_size=${PAGE_SIZE}&page=${pageNumber}&c=${Date.now()}&${
      timestamp ? `last_pulled_at=${timestamp}` : ''
    }`,
  );

  const data = response.data;
  let lastResult = true;

  const mappedData = {
    // changed the key from studentsData to studentsDataV2
    studentsDataV2: data.studentsData,
    checkpoint: data.checkpoint,
    checkpointResults: data.checkpointResults,
    checkpointAssessments: data.checkpointAssessments,
    usersDataV2: data.usersData,
    studentsAssessmentData: data.studentsAssessmentData,
    checkedStudentExam: data.checkedStudentExam,
    studentSubjectAnswerAnalysis: data.studentSubjectAnswerAnalysis,
    checkedStudentExamV2: data.checkedStudentExam,
    timestamp: data.timestamp,
  };
  const mapStudentsData = (studentsData: any[]) =>
    studentsData.map((student) => ({ ...student, userId: student?.user }));
  mappedData.studentsDataV2.created.data = mapStudentsData(
    mappedData.studentsDataV2.created.data,
  );
  mappedData.studentsDataV2.updated.data = mapStudentsData(
    mappedData.studentsDataV2.updated.data,
  );

  const models = [
    'checkpoint',
    'checkpointResults',
    'checkpointAssessments',
    'studentsDataV2',
    'studentsAssessmentData',
    'usersDataV2',
    'checkedStudentExam',
    'studentSubjectAnswerAnalysis',
    'checkedStudentExamV2',
  ] as const;

  try {
    // we need to map the studentsData to userId to avoid conflicts with the user model

    const addDataToDb = await extractSyncResponse(
      models,
      mappedData,
      studentDB,
    );
    console.log(`Added ${models} in db ${addDataToDb}`);
  } catch (e) {
    lastResult = false;
    console.error(`Unable to add data for ${models} ${e}`);
  }

  const modelCounts = getModelCount(models, mappedData);

  const maxPages = Math.max(...modelCounts) / PAGE_SIZE;

  if (pageNumber <= maxPages) {
    // fetch the next page.
    lastResult =
      lastResult &&
      (await fetchStudentAssessmentDataV1(timestamp, pageNumber + 1));
  }
  // remove the local new created records - avoid duplicates
  studentDB.removeNewOrDeletedRecords();
  return lastResult;
};
